/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Workspace Settings & Team Management View (Task 10.1.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useTeamStore, type WorkspaceMemberRole } from '../stores/useTeamStore';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useWorkspacePermissions } from '../hooks/useWorkspacePermissions';
import { useUiStore } from '../stores/useUiStore';
import { useI18nStore } from '../stores/useI18nStore';
import { getWorkspaceDomains } from '../lib/urls';
import {
  Users,
  Building,
  CreditCard,
  UserPlus,
  Shield,
  Trash2,
  Check,
  AlertCircle,
  Copy,
  ExternalLink,
  Crown,
  Sparkles,
  Eye,
  Palette,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { useTheme } from '../context/ThemeProvider';

export const WorkspaceSettingsView: React.FC = () => {
  const { t } = useI18nStore();
  const [activeTab, setActiveTab] = useState<'members' | 'general' | 'subscription' | 'theme'>('members');
  const { themeMode, setThemeMode, themeData, isDark } = useTheme();
  const {
    members,
    invitations,
    subscription,
    updateMemberRole,
    removeMember,
    cancelInvitation,
  } = useTeamStore();

  const { getActiveWorkspace, setWorkspaces, workspaces, activeWorkspaceId } = useWorkspaceStore();
  const permissions = useWorkspacePermissions();
  const { openModal } = useUiStore();

  const activeWorkspace = getActiveWorkspace();

  // General Settings Form State
  const [workspaceName, setWorkspaceName] = useState(activeWorkspace?.name || 'My Workspace');
  const [workspaceSlug, setWorkspaceSlug] = useState(activeWorkspace?.slug || 'my-workspace');
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const handleSaveGeneral = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeWorkspaceId) return;

    const updated = workspaces.map((w) =>
      w.id === activeWorkspaceId
        ? { ...w, name: workspaceName, slug: workspaceSlug }
        : w,
    );
    setWorkspaces(updated);
    setSaveFeedback(t('settings.generalSaved'));
    setTimeout(() => setSaveFeedback(null), 3000);
  };

  const handleRoleChange = (memberId: string, newRole: 'admin' | 'member' | 'viewer') => {
    updateMemberRole(memberId, newRole);
  };

  const handleRemoveMember = (memberId: string) => {
    if (window.confirm(t('settings.confirmRemoveMember'))) {
      removeMember(memberId);
    }
  };

  const handleCopyInviteToken = (token: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : getWorkspaceDomains().appOrigin;
    const link = `${origin}/invite?token=${token}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(link);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    }
  };

  const getRoleBadge = (role: WorkspaceMemberRole) => {
    switch (role) {
      case 'owner':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-500 border border-amber-500/30 uppercase tracking-wide">
            <Crown className="w-3 h-3" />
            <span>{t('settings.roleOwner')}</span>
          </span>
        );
      case 'admin':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary border border-primary/30 uppercase tracking-wide">
            <Shield className="w-3 h-3" />
            <span>{t('settings.roleAdmin')}</span>
          </span>
        );
      case 'viewer':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-muted text-foreground-muted border border-surface-border uppercase tracking-wide">
            <Eye className="w-3 h-3" />
            <span>{t('settings.roleViewer')}</span>
          </span>
        );
      case 'member':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 uppercase tracking-wide">
            <Sparkles className="w-3 h-3" />
            <span>{t('settings.roleMember')}</span>
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Header Overview Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 rounded-2xl border border-surface-border bg-surface shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Building className="w-5 h-5 text-primary" />
            <h1 className="font-heading font-bold text-xl text-foreground">
              {activeWorkspace?.name ?? t('settings.title')}
            </h1>
          </div>
          <p className="text-xs text-foreground-muted font-mono">
            ID: {activeWorkspaceId} | <strong className="text-primary uppercase">{permissions.role}</strong>
          </p>
        </div>

        {permissions.canManageMembers && (
          <button
            type="button"
            onClick={() => openModal('invite_member')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover active:scale-[0.98] transition-all shadow-md self-start sm:self-auto"
          >
            <UserPlus className="w-4 h-4" />
            <span>{t('team.inviteTitle')}</span>
          </button>
        )}
      </div>

      {/* 2. Navigation Tabs Strip */}
      <div className="flex items-center gap-2 border-b border-surface-border pb-2 text-xs">
        <button
          type="button"
          onClick={() => setActiveTab('members')}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl font-medium transition-all ${
            activeTab === 'members'
              ? 'bg-primary/10 text-primary font-bold shadow-sm'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>{t('settings.tabs.members')} ({members.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl font-medium transition-all ${
            activeTab === 'general'
              ? 'bg-primary/10 text-primary font-bold shadow-sm'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
          }`}
        >
          <Building className="w-4 h-4" />
          <span>{t('settings.tabs.general')}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('subscription')}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl font-medium transition-all ${
            activeTab === 'subscription'
              ? 'bg-primary/10 text-primary font-bold shadow-sm'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          <span>{t('settings.tabs.subscription')}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('theme')}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl font-medium transition-all ${
            activeTab === 'theme'
              ? 'bg-primary/10 text-primary font-bold shadow-sm'
              : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
          }`}
        >
          <Palette className="w-4 h-4" />
          <span>{t('settings.tabs.theme')}</span>
        </button>
      </div>

      {/* 3. Tab Contents */}

      {/* TAB 1: MEMBERS & RBAC */}
      {activeTab === 'members' && (
        <div className="space-y-6">
          {/* Members Table Card */}
          <div className="rounded-2xl border border-surface-border bg-surface overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-surface-border/80 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm text-foreground">{t('settings.membersSectionTitle')}</h3>
                <p className="text-xs text-foreground-muted">
                  {t('settings.membersSectionDesc')}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/40 border-b border-surface-border/60 text-foreground-muted uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="py-3 px-4">{t('team.roleMember')}</th>
                    <th className="py-3 px-4">{t('team.roleLabel')}</th>
                    <th className="py-3 px-4">{t('common.status')}</th>
                    <th className="py-3 px-4 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border/40">
                  {members.map((member) => {
                    const initials = member.fullName
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase();

                    const isOwnerMember = member.role === 'owner';

                    return (
                      <tr key={member.id} className="hover:bg-muted/20 transition-colors">
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold flex items-center justify-center text-xs flex-shrink-0">
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-foreground truncate">{member.fullName}</div>
                              <div className="text-[11px] text-foreground-muted font-mono truncate">{member.email}</div>
                            </div>
                          </div>
                        </td>

                        <td className="py-3.5 px-4">
                          {isOwnerMember || !permissions.canManageMembers ? (
                            getRoleBadge(member.role)
                          ) : (
                            <select
                              value={member.role}
                              onChange={(e) =>
                                handleRoleChange(
                                  member.id,
                                  e.target.value as 'admin' | 'member' | 'viewer',
                                )
                              }
                              className="px-2.5 py-1 rounded-lg border border-surface-border bg-surface-elevated text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-medium"
                            >
                              <option value="admin">{t('settings.roleAdmin')}</option>
                              <option value="member">{t('settings.roleMember')}</option>
                              <option value="viewer">{t('settings.roleViewer')}</option>
                            </select>
                          )}
                        </td>

                        <td className="py-3.5 px-4 font-mono text-[11px] text-foreground-muted">
                          {member.joinedAt}
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          {!isOwnerMember && permissions.canManageMembers ? (
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(member.id)}
                              className="p-1.5 rounded-lg text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                              title={t('settings.removeMember')}
                              aria-label={t('settings.removeMember')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-[10px] text-foreground-muted italic">{t('common.info')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pending Invitations Card */}
          {invitations.length > 0 && (
            <div className="rounded-2xl border border-surface-border bg-surface overflow-hidden shadow-sm">
              <div className="px-5 py-3.5 border-b border-surface-border/80">
                <h3 className="font-bold text-sm text-foreground">{t('settings.pendingInvites', { count: invitations.length })}</h3>
              </div>

              <div className="divide-y divide-surface-border/40">
                {invitations.map((inv) => (
                  <div key={inv.id} className="p-4 flex items-center justify-between gap-4 text-xs">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">{inv.email}</div>
                      <div className="text-[11px] text-foreground-muted">
                        <strong className="uppercase">{inv.role}</strong> | {inv.expiresAt}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleCopyInviteToken(inv.token)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                          copiedToken === inv.token
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
                            : 'border-surface-border bg-surface-elevated text-foreground hover:bg-muted'
                        }`}
                      >
                        {copiedToken === inv.token ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        <span>{copiedToken === inv.token ? t('team.inviteLinkCopied') : t('team.copyInviteLink')}</span>
                      </button>

                      {permissions.canManageMembers && (
                        <button
                          type="button"
                          onClick={() => cancelInvitation(inv.id)}
                          className="px-2.5 py-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 text-xs font-medium transition-colors"
                        >
                          {t('settings.revokeInvite')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: GENERAL SETTINGS */}
      {activeTab === 'general' && (
        <form onSubmit={handleSaveGeneral} className="space-y-6 max-w-2xl">
          <div className="p-6 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-sm">
            <h3 className="font-bold text-sm text-foreground">{t('settings.generalSectionTitle')}</h3>

            {saveFeedback && (
              <div className="p-3.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 text-xs font-medium flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" />
                <span>{saveFeedback}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="ws-name" className="block text-xs font-semibold text-foreground">
                {t('settings.workspaceName')}
              </label>
              <input
                id="ws-name"
                type="text"
                disabled={!permissions.canEditSettings}
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="ws-slug" className="block text-xs font-semibold text-foreground">
                {t('settings.workspaceSlug')}
              </label>
              <div className="flex items-center">
                <span className="px-3 py-2.5 rounded-l-xl border border-r-0 border-surface-border bg-muted/60 text-xs text-foreground-muted font-mono">
                  {getWorkspaceDomains().appOrigin.replace(/^https?:\/\//, '')}/
                </span>
                <input
                  id="ws-slug"
                  type="text"
                  disabled={!permissions.canEditSettings}
                  value={workspaceSlug}
                  onChange={(e) => setWorkspaceSlug(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-r-xl border border-surface-border bg-surface-elevated text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
              </div>
            </div>

            {permissions.canEditSettings && (
              <div className="pt-2">
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover active:scale-[0.98] transition-all shadow-md"
                >
                  {t('settings.saveGeneralBtn')}
                </button>
              </div>
            )}
          </div>

          {/* Danger Zone: Only available for Owner */}
          {permissions.isOwner && (
            <div className="p-6 rounded-2xl border border-rose-500/30 bg-rose-500/5 space-y-3">
              <div className="flex items-center gap-2 text-rose-500 font-bold text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{t('settings.dangerZoneTitle')}</span>
              </div>
              <p className="text-xs text-foreground-muted">
                {t('settings.deleteWorkspaceWarning')}
              </p>
              <button
                type="button"
                onClick={() => alert(t('settings.deleteWorkspaceWarning'))}
                className="px-4 py-2 rounded-xl bg-rose-500 text-white text-xs font-semibold hover:bg-rose-600 transition-colors"
              >
                {t('settings.deleteWorkspace')}
              </button>
            </div>
          )}
        </form>
      )}

      {/* TAB 3: SUBSCRIPTION & PLAN */}
      {activeTab === 'subscription' && (
        <div className="space-y-6 max-w-3xl">
          <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-surface-border pb-5">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                  {t('settings.activePlan')}
                </span>
                <h3 className="font-heading font-bold text-lg text-foreground mt-0.5">
                  {subscription?.planName || t('settings.currentPlan')}
                </h3>
                <p className="text-xs text-foreground-muted">
                  {subscription?.currentPeriodEnd}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 uppercase">
                  {subscription?.status || 'Active'}
                </span>
                {permissions.canManageBilling && (
                  <button
                    type="button"
                    onClick={() => openModal('topup_credits')}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover transition-colors shadow-sm"
                  >
                    <span>{t('wallet.topUp')}</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Features Checklist */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-foreground">{t('settings.currentPlan')}:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-foreground-muted">
                {subscription?.features.map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: THEME & BRANDING */}
      {activeTab === 'theme' && (
        <div className="space-y-6 max-w-4xl animate-fade-in">
          {/* 1. Theme Mode Switcher Card */}
          <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
            <div>
              <h3 className="font-bold text-sm text-foreground">{t('settings.activeThemeMode')}</h3>
              <p className="text-xs text-foreground-muted">{t('settings.themeSectionDesc')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setThemeMode('light')}
                className={`flex items-center gap-3 p-3.5 rounded-xl border text-xs font-semibold transition-all ${
                  themeMode === 'light'
                    ? 'border-primary bg-primary/10 text-primary shadow-sm'
                    : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Sun className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block font-bold">{t('header.light')}</span>
                  <span className="text-[10px] text-foreground-muted font-normal">Light Palette</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setThemeMode('dark')}
                className={`flex items-center gap-3 p-3.5 rounded-xl border text-xs font-semibold transition-all ${
                  themeMode === 'dark'
                    ? 'border-primary bg-primary/10 text-primary shadow-sm'
                    : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Moon className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block font-bold">{t('header.dark')}</span>
                  <span className="text-[10px] text-foreground-muted font-normal">OLED Dark Palette</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setThemeMode('system')}
                className={`flex items-center gap-3 p-3.5 rounded-xl border text-xs font-semibold transition-all ${
                  themeMode === 'system'
                    ? 'border-primary bg-primary/10 text-primary shadow-sm'
                    : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-muted text-foreground-muted flex items-center justify-center">
                  <Monitor className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block font-bold">{t('header.system')}</span>
                  <span className="text-[10px] text-foreground-muted font-normal">OS Dynamic Mode</span>
                </div>
              </button>
            </div>
          </div>

          {/* 2. Active Theme Color Tokens Palette */}
          <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
            <div>
              <h3 className="font-bold text-sm text-foreground">{t('settings.activeThemePalette')}</h3>
              <p className="text-xs text-foreground-muted">
                {isDark ? 'Dark Mode (Active Tokens)' : 'Light Mode (Active Tokens)'}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Primary Token */}
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div className="w-full h-10 rounded-lg bg-primary border border-white/20 shadow-inner" />
                <div>
                  <span className="block text-xs font-bold text-foreground">{t('settings.tokenPrimary')}</span>
                  <span className="text-[10px] font-mono text-foreground-muted">--color-primary</span>
                </div>
              </div>

              {/* Accent Token */}
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div className="w-full h-10 rounded-lg bg-accent border border-white/20 shadow-inner" />
                <div>
                  <span className="block text-xs font-bold text-foreground">{t('settings.tokenAccent')}</span>
                  <span className="text-[10px] font-mono text-foreground-muted">--color-accent</span>
                </div>
              </div>

              {/* Surface Token */}
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div className="w-full h-10 rounded-lg bg-surface border border-surface-border shadow-inner" />
                <div>
                  <span className="block text-xs font-bold text-foreground">{t('settings.tokenSurface')}</span>
                  <span className="text-[10px] font-mono text-foreground-muted">--color-surface</span>
                </div>
              </div>

              {/* Base Background Token */}
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div className="w-full h-10 rounded-lg bg-background-base border border-surface-border shadow-inner" />
                <div>
                  <span className="block text-xs font-bold text-foreground">{t('settings.tokenBackground')}</span>
                  <span className="text-[10px] font-mono text-foreground-muted">--color-bg-base</span>
                </div>
              </div>

              {/* Border Token */}
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div className="w-full h-10 rounded-lg border-4 border-surface-border bg-surface shadow-inner" />
                <div>
                  <span className="block text-xs font-bold text-foreground">{t('settings.tokenBorder')}</span>
                  <span className="text-[10px] font-mono text-foreground-muted">--color-border</span>
                </div>
              </div>

              {/* Text Foreground Token */}
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div className="w-full h-10 rounded-lg bg-foreground border border-surface-border flex items-center justify-center text-background-base font-bold text-xs">
                  Aa
                </div>
                <div>
                  <span className="block text-xs font-bold text-foreground">{t('settings.tokenText')}</span>
                  <span className="text-[10px] font-mono text-foreground-muted">--color-text-main</span>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Dynamic Gradients Card */}
          <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
            <div>
              <h3 className="font-bold text-sm text-foreground">{t('settings.activeGradients')}</h3>
              <p className="text-xs text-foreground-muted">Edge-generated CSS gradient definitions</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div
                  className="w-full h-14 rounded-lg shadow-sm"
                  style={{
                    background:
                      'linear-gradient(135deg, hsl(var(--color-primary)) 0%, hsl(var(--color-accent)) 100%)',
                  }}
                />
                <span className="block text-xs font-bold text-foreground">Primary Gradient</span>
                <span className="text-[10px] font-mono text-foreground-muted block truncate">
                  primary -{'>'} accent
                </span>
              </div>

              <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div
                  className="w-full h-14 rounded-lg shadow-sm"
                  style={{
                    background:
                      'linear-gradient(135deg, hsl(var(--color-accent)) 0%, #10B981 100%)',
                  }}
                />
                <span className="block text-xs font-bold text-foreground">Accent Gradient</span>
                <span className="text-[10px] font-mono text-foreground-muted block truncate">
                  accent -{'>'} emerald
                </span>
              </div>

              <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
                <div
                  className="w-full h-14 rounded-lg shadow-sm"
                  style={{
                    background:
                      'radial-gradient(at 0% 0%, hsl(var(--color-primary)/0.2) 0px, transparent 50%)',
                  }}
                />
                <span className="block text-xs font-bold text-foreground">Mesh Ambient</span>
                <span className="text-[10px] font-mono text-foreground-muted block truncate">
                  radial-gradient ambient
                </span>
              </div>
            </div>
          </div>

          {/* 4. Edge Branding Metadata */}
          <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-3">
            <h3 className="font-bold text-sm text-foreground">{t('settings.systemBranding')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated">
                <span className="text-foreground-muted block">{t('settings.appName')}</span>
                <span className="font-bold text-foreground mt-0.5 block">
                  {themeData?.branding?.siteName || 'Sophia Platform'}
                </span>
              </div>
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated">
                <span className="text-foreground-muted block">{t('settings.edgeEndpoint')}</span>
                <span className="font-mono text-primary mt-0.5 block font-semibold truncate">
                  /api/v1/public/theme
                </span>
              </div>
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated">
                <span className="text-foreground-muted block">{t('settings.edgeCacheStatus')}</span>
                <span className="font-semibold text-emerald-500 mt-0.5 block flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Synced (HTTP 200)
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
